import { toastr } from 'react-redux-toastr';
import { parseErrorMessage } from 'common/helpers/errors';
import {
  ALL, RELEASE_SCOPE, TEMPLATE_UNAVAILABLE, releaseNotesConstants as C
} from '../constants/releaseNotes.constants';
import * as service from './releaseNotes.service';

// What the user is told, through the toasts the rest of the app uses. (The
// `alert` store is drawn only on the sign-in pages, so nothing sent there
// would ever show here.)
export const notifySuccess = message => () => { toastr.success(message); };
export const notifyError = message => () => { toastr.error(message); };

let lastRequestId = 0;
const nextRequestId = () => {
  lastRequestId += 1;
  return lastRequestId;
};

// Runs one async flow: REQUEST, then SUCCESS with the data or FAILURE with an
// error toast. Each request carries an id so a reducer can drop a response
// that arrives after a newer request for the same slice. The returned promise
// resolves to the data, or to null on failure (or what `onFailure` makes of the
// error), so callers never see a rejection.
const runRequest = (types, call, {
  toastOnError = true, successMessage, meta = {}, onFailure
} = {}) => (dispatch) => {
  const [requestType, successType, failureType] = types;
  const requestId = nextRequestId();
  dispatch({ type: requestType, requestId, ...meta });

  return call().then(
    (data) => {
      dispatch({ type: successType, requestId, data, ...meta });
      if (successMessage) dispatch(notifySuccess(successMessage));
      return data;
    },
    (error) => {
      const message = parseErrorMessage(error);
      dispatch({
        // ApiError carries the HTTP status as `code`.
        type: failureType, requestId, error: message, status: error && error.code, ...meta
      });
      if (toastOnError) dispatch(notifyError(message));
      return onFailure ? onFailure(error) : null;
    }
  );
};

const typesFor = name => [C[`${name}_REQUEST`], C[`${name}_SUCCESS`], C[`${name}_FAILURE`]];

// The reader shows live notes only: it sends no status, which the API reads as
// the reader's view for every role.
export const getReaderNotes = () => runRequest(
  typesFor('GET_READER_NOTES'), () => service.getReleaseNotes({}), { toastOnError: false }
);

export const getManageNotes = () => runRequest(
  typesFor('GET_MANAGE_NOTES'), () => service.getReleaseNotes({ status: ALL }), { toastOnError: false }
);

export const getNote = noteId => runRequest(
  typesFor('GET_NOTE'), () => service.getReleaseNote(noteId), { toastOnError: false }
);

export const clearNote = () => ({ type: C.CLEAR_NOTE });

// The reader lists live releases, the manage page and editor every release;
// each scope fills its own slice.
export const getReleases = (scope = RELEASE_SCOPE.LIVE) => runRequest(
  typesFor('GET_RELEASES'), () => service.getReleases(scope), { toastOnError: false, meta: { scope } }
);

export const saveNote = (noteId, note, successMessage) => runRequest(
  typesFor('SAVE_NOTE'),
  () => (noteId ? service.updateReleaseNote(noteId, note) : service.createReleaseNote(note)),
  { successMessage }
);

export const deleteNote = (noteId, successMessage) => runRequest(
  typesFor('DELETE_NOTE'), () => service.deleteReleaseNote(noteId), { successMessage, meta: { noteId } }
);

export const bulkUpdateNotes = (ids, action, successMessage) => runRequest(
  typesFor('BULK_NOTES'), () => service.bulkUpdateNotes(ids, action), { successMessage }
);

export const saveRelease = (releaseId, release, successMessage) => runRequest(
  typesFor('SAVE_RELEASE'), () => service.updateRelease(releaseId, release), { successMessage }
);

export const deleteRelease = (releaseId, successMessage) => runRequest(
  typesFor('DELETE_RELEASE'), () => service.deleteRelease(releaseId), { successMessage }
);

export const getUnreadCount = () => runRequest(
  typesFor('GET_UNREAD_COUNT'), service.getUnreadCount, { toastOnError: false }
);

export const uploadImage = (file, onProgress) => runRequest(
  typesFor('UPLOAD_IMAGE'), () => service.uploadImage(file, onProgress)
);

// The From Petasense list: every Petasense note, filtered on the page, so the
// counts on its filter tabs and the list always agree.
export const getPetasenseNotes = () => runRequest(
  typesFor('GET_PETASENSE_NOTES'), () => service.getPetasenseNotes(ALL), { toastOnError: false }
);

// Not relevant, or undo either decision; the list is fetched again after.
export const decidePetasenseNote = (noteId, skip, successMessage) => dispatch => dispatch(runRequest(
  typesFor('DECIDE_PETASENSE_NOTE'),
  () => (skip ? service.skipPetasenseNote(noteId) : service.undoPetasenseDecision(noteId)),
  { successMessage, meta: { noteId } }
)).then((done) => {
  dispatch(getPetasenseNotes());
  return done;
});

// A note that is gone, no longer live, or not the caller's to read (404, 403)
// will never load, so it resolves to TEMPLATE_UNAVAILABLE rather than null,
// which means a retry may work. The editor says which; no toast.
export const getTemplate = noteId => runRequest(
  typesFor('GET_TEMPLATE'), () => service.getPetasenseTemplate(noteId), {
    toastOnError: false,
    onFailure: error => (error && [403, 404].indexOf(error.code) !== -1 ? TEMPLATE_UNAVAILABLE : null)
  }
);

// The dot clears as soon as this is asked. If the server did not record it,
// the count is fetched again so the dot comes back rather than lying.
export const markSeen = () => dispatch => dispatch(runRequest(
  typesFor('MARK_SEEN'), service.markSeen, { toastOnError: false }
)).then(done => done || dispatch(getUnreadCount()));
